import React, {useContext, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import toast from "react-hot-toast";

import FormControl from "../../../components/form/FormControl.jsx";
import FormContainer from "../../../components/form/FormContainer.jsx";

import {ProjectApi} from "../../../api/authority/generated/index.ts";
import {useErrorTranslation} from "../../../api/utilities/translateErrorMessage.js";
import {GlobalContext} from "../../../utils/context/GlobalContextProvider.jsx";
import useDownloadState from "../../../api/utilities/useDownloadState.js";
import Button from "../../../components/Button.jsx";

export default function BillingDataForm({project, onSave}) {
    const {t} = useTranslation();
    const {et} = useErrorTranslation();
    const {context, getAuthorityConfiguration} = useContext(GlobalContext);

    const fieldDefinitions = [
        {name: "email", required: true, translation: t("billing.form.email")},
        {name: "companyName", required: true, translation: t("billing.form.companyName")},
        {name: "street", translation: t("billing.form.street")},
        {name: "city", required: true, translation: t("billing.form.city")},
        {name: "zip", required: true, translation: t("billing.form.zip")},
        {name: "companyRegistrationNumber", translation: t("billing.form.companyRegistrationNumber")},
        {name: "vatNumber", translation: t("billing.form.vatNumber")},
    ];

    let initialState = useMemo(() => {
        const initialState = fieldDefinitions.reduce((acc, field) => {
            acc[field.name] = project.billingAccount?.[field.name] || "";
            return acc;
        }, {});

        // Set email default value
        initialState.email = project.billingAccount?.email || context.currentIdentity?.user?.email || "";

        return initialState;
    }, [project.id]);

    // Initialize billing account input state
    const [billingAccountInput, setBillingAccountInput] = useState(initialState);

    useEffect(() => {
        setBillingAccountInput(initialState);
    }, [project.id]);

    const [isFormValid, setIsFormValid] = useState(false);
    const [saveState, setDownloading, setResult, setError] = useDownloadState();

    // Validate the form based on required fields
    useEffect(() => {
        const requiredFields = fieldDefinitions.filter((field) => field.required).map((field) => field.name);
        const allFilled = requiredFields.every(
            (field) => billingAccountInput[field] && billingAccountInput[field].trim()
        );
        setIsFormValid(allFilled);
    }, [billingAccountInput]);

    // Handle saving billing data
    const handleSaveBillingData = async () => {
        if (!isFormValid) {
            toast.error(t("form.fill_required_fields"));
            return;
        }

        setDownloading(true);
        try {
            const response = await new ProjectApi(getAuthorityConfiguration()).changeBillingAccount({
                projectId: project.id,
                billingAccountInput,
            });

            setResult(response);
            toast.success(t("saved"));

            if (typeof onSave === "function") {
                onSave(response);
            }
        } catch (e) {
            const errorMessage = await et(e);
            toast.error(errorMessage);
        } finally {
            setDownloading(false);
        }
    };

    // Handle field changes
    const handleFieldChange = (fieldName, value) => {
        setBillingAccountInput((prevState) => ({
            ...prevState,
            [fieldName]: value,
        }));
    };

    return (
        <FormContainer>
            <div className="grid gap-4 sm:grid-cols-2">
                {fieldDefinitions.map((field) => (
                    <div
                        key={field.name}
                        className={['email', 'companyName', 'street'].includes(field.name) ? 'sm:col-span-2' : ''}
                    >
                    <FormControl
                        type={field.type || "text"}
                        label={`${field.translation}${field.required ? "*" : ""}`}
                        name={field.name}
                        variant="modern"
                        value={billingAccountInput[field.name]}
                        required={field.required || false}
                        onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    />
                        {field.name === 'email' && <p className="mt-1.5 text-xs leading-5 text-slate-500 dark:text-slate-400">
                            {t("billing.form.email-caption")}
                        </p>}
                    </div>
                ))}
            </div>

            {/* Save Button */}
            <div className="mt-5 flex justify-end">
                <Button
                    size="normal"
                    onClick={handleSaveBillingData}
                    disabled={!isFormValid || saveState.downloading}
                >
                    {t('save')}
                </Button>
            </div>
        </FormContainer>
    );
}
